import type { LibraryItem, SiteFaviconDescriptor } from '@kiipu/app-protocol';
import { SiteFaviconCache } from './site-favicon-cache';

const API_PATH = '/api/v1/site-favicons';
const CONTENT =
  /^\/api\/v1\/site-favicons\/[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\/content\?v=[a-f0-9]{64}$/i;
export function sourceHost(item: Pick<LibraryItem, 'url' | 'sourceHost'>): string {
  try {
    return normalizeHost(item.sourceHost || new URL(item.url).hostname);
  } catch {
    return '';
  }
}
function normalizeHost(value: string) {
  if (
    !value ||
    value.length > 253 ||
    /[\s/@?#\\]/.test(value) ||
    (value.includes(':') && !(value.startsWith('[') && value.endsWith(']')))
  )
    return '';
  try {
    return new URL(`https://${value}`).hostname.toLowerCase().replace(/\.+$/, '');
  } catch {
    return '';
  }
}
/** Split by both host count and encoded URL length; long IDNs cannot exceed the API boundary. */
export function faviconBatches(hosts: string[]): string[] {
  const paths: string[] = [];
  let values: string[] = [];
  for (const host of [...new Set(hosts.map(normalizeHost).filter(Boolean))]) {
    const next = [...values, host];
    const path = `${API_PATH}?${new URLSearchParams(next.map((value) => ['host', value]))}`;
    if (values.length && (next.length > 50 || path.length > 4096)) {
      paths.push(`${API_PATH}?${new URLSearchParams(values.map((value) => ['host', value]))}`);
      values = [host];
    } else values = next;
  }
  if (values.length)
    paths.push(`${API_PATH}?${new URLSearchParams(values.map((value) => ['host', value]))}`);
  return paths;
}
export function faviconContentPath(value: string | null, origin: string) {
  if (!value) return null;
  try {
    const url = new URL(value, origin);
    return url.origin === new URL(origin).origin &&
      !url.hash &&
      !url.username &&
      !url.password &&
      CONTENT.test(url.pathname + url.search)
      ? url.pathname + url.search
      : null;
  } catch {
    return null;
  }
}
export function validFavicon(value: unknown): value is SiteFaviconDescriptor {
  if (!value || typeof value !== 'object') return false;
  const entry = value as SiteFaviconDescriptor;
  return (
    typeof entry.host === 'string' &&
    normalizeHost(entry.host) === entry.host &&
    ['absent', 'queued', 'fetching', 'ready', 'unavailable'].includes(entry.status) &&
    Number.isSafeInteger(entry.version) &&
    entry.version >= 0 &&
    (entry.faviconUrl === null || typeof entry.faviconUrl === 'string') &&
    (entry.hash === null || /^[a-f0-9]{64}$/.test(entry.hash)) &&
    (entry.retryAt === null || typeof entry.retryAt === 'string')
  );
}
type Dependencies = {
  fetcher: typeof fetch;
  cache: SiteFaviconCache;
  now: () => number;
};
/** One store per renderer, with one request per visible host and no ready-state polling. */
export class SiteFaviconStore {
  private partition = '';
  private origin = '';
  private generation = 0;
  private online = true;
  private foreground = true;
  private descriptors = new Map<string, SiteFaviconDescriptor>();
  private listeners = new Map<string, Set<() => void>>();
  private visible = new Map<string, number>();
  private next = new Map<string, number>();
  private started = new Map<string, number>();
  private pending = new Set<string>();
  private revalidate = new Set<string>();
  private controller = new AbortController();
  private timer: ReturnType<typeof setTimeout> | undefined;
  private images = new Map<string, string>();
  private imageJobs = new Map<string, Promise<string | null>>();
  private resumes = new Set<() => void>();
  constructor(
    private dependencies: Dependencies = {
      fetcher: (...args) => fetch(...args),
      cache: new SiteFaviconCache(),
      now: Date.now,
    },
  ) {}
  get(host: string) {
    return this.descriptors.get(host);
  }
  identity() {
    return this.generation;
  }
  onResume(listener: () => void) {
    this.resumes.add(listener);
    return () => {
      this.resumes.delete(listener);
    };
  }
  subscribe(host: string, listener: () => void) {
    let listeners = this.listeners.get(host);
    if (!listeners) this.listeners.set(host, (listeners = new Set()));
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
      if (!listeners.size) this.listeners.delete(host);
    };
  }
  show(host: string) {
    if (!host) return () => undefined;
    this.visible.set(host, (this.visible.get(host) ?? 0) + 1);
    this.schedule();
    return () => {
      const count = (this.visible.get(host) ?? 1) - 1;
      if (count > 0) this.visible.set(host, count);
      else this.visible.delete(host);
      this.schedule();
    };
  }
  identify(origin: string, account: string) {
    const partition = account ? JSON.stringify([origin, account]) : '';
    if (partition === this.partition) return;
    this.generation++;
    this.controller.abort();
    this.controller = new AbortController();
    this.partition = partition;
    this.origin = origin;
    this.descriptors.clear();
    this.next.clear();
    this.started.clear();
    this.pending.clear();
    this.revalidate.clear();
    for (const url of this.images.values()) URL.revokeObjectURL(url);
    this.images.clear();
    this.imageJobs.clear();
    for (const listeners of this.listeners.values()) for (const listener of listeners) listener();
    const generation = this.generation;
    if (partition)
      void this.dependencies.cache.descriptors(partition).then((items) => {
        if (generation !== this.generation) return;
        for (const item of items) if (validFavicon(item)) this.accept(item);
        this.schedule();
      });
    this.schedule();
  }
  setAvailability(online: boolean, foreground: boolean) {
    const resumed = (!this.online && online) || (!this.foreground && foreground);
    this.online = online;
    this.foreground = foreground;
    if (resumed) {
      this.refresh(true);
    } else this.schedule();
  }
  refresh(retryImages = false) {
    for (const host of this.visible.keys()) {
      if (retryImages && this.get(host)?.status === 'ready') this.revalidate.add(host);
      if (this.get(host)?.status !== 'ready' || this.revalidate.has(host)) this.next.delete(host);
    }
    this.schedule();
    if (retryImages) for (const listener of this.resumes) listener();
  }
  contentFailed(host: string) {
    const entry = this.get(host);
    if (!this.partition || !entry) return;
    this.revalidate.add(host);
    this.next.delete(host);
    void this.dependencies.cache.remove(this.partition, 'descriptor', host);
    const path = faviconContentPath(entry.faviconUrl, this.origin);
    if (path) {
      const url = this.images.get(path);
      if (url) URL.revokeObjectURL(url);
      this.images.delete(path);
      void this.dependencies.cache.remove(this.partition, 'image', path);
    }
    this.schedule();
  }
  private accept(item: SiteFaviconDescriptor) {
    const previous = this.descriptors.get(item.host);
    if (previous && previous.version > item.version) return;
    if (previous && JSON.stringify(previous) === JSON.stringify(item)) return;
    if (
      (item.status === 'queued' || item.status === 'fetching') &&
      previous?.status !== 'queued' &&
      previous?.status !== 'fetching'
    )
      this.started.set(item.host, this.dependencies.now());
    this.descriptors.set(item.host, item);
    for (const listener of this.listeners.get(item.host) ?? []) listener();
  }
  private schedule() {
    clearTimeout(this.timer);
    this.timer = undefined;
    if (!this.partition || !this.online || !this.foreground) return;
    const hosts = [...this.visible.keys()].filter(
      (host) =>
        (this.get(host)?.status !== 'ready' || this.revalidate.has(host)) &&
        !this.pending.has(host),
    );
    if (!hosts.length) return;
    const delay = Math.max(
      0,
      Math.min(...hosts.map((host) => this.next.get(host) ?? 0)) - this.dependencies.now(),
    );
    this.timer = setTimeout(() => {
      void this.poll();
    }, delay || 20);
  }
  private async poll() {
    if (!this.partition || !this.online || !this.foreground) return;
    const generation = this.generation,
      partition = this.partition,
      now = this.dependencies.now();
    const hosts = [...this.visible.keys()].filter(
      (host) =>
        (this.get(host)?.status !== 'ready' || this.revalidate.has(host)) &&
        !this.pending.has(host) &&
        (this.next.get(host) ?? 0) <= now,
    );
    for (const host of hosts) {
      this.pending.add(host);
      if (!this.started.has(host)) this.started.set(host, now);
    }
    await Promise.allSettled(
      faviconBatches(hosts).map(async (path) => {
        const requested = new Set(new URL(path, this.origin).searchParams.getAll('host'));
        const response = await this.dependencies.fetcher(path, {
          credentials: 'include',
          signal: AbortSignal.any([this.controller.signal, AbortSignal.timeout(15_000)]),
          redirect: 'error',
        });
        if (!response.ok) return;
        const value = (await response.json()) as { items?: unknown[] };
        if (generation !== this.generation || !Array.isArray(value.items)) return;
        for (const item of value.items)
          if (validFavicon(item) && requested.has(item.host)) {
            this.accept(item);
            this.revalidate.delete(item.host);
            void this.dependencies.cache.save(partition, 'descriptor', item.host, item);
          }
      }),
    );
    if (generation !== this.generation) return;
    for (const host of hosts) {
      this.pending.delete(host);
      const entry = this.get(host);
      const fast =
        (entry?.status === 'queued' || entry?.status === 'fetching') &&
        this.dependencies.now() - (this.started.get(host) ?? now) < 60_000;
      this.next.set(host, this.dependencies.now() + (fast ? 5000 : 60_000));
    }
    this.schedule();
  }
  image(descriptor: SiteFaviconDescriptor): Promise<string | null> {
    const path = faviconContentPath(descriptor.faviconUrl, this.origin);
    if (
      !this.partition ||
      descriptor.status !== 'ready' ||
      !path ||
      !descriptor.hash ||
      !path.endsWith(`?v=${descriptor.hash}`)
    )
      return Promise.resolve(null);
    const cached = this.images.get(path);
    if (cached) return Promise.resolve(cached);
    const pending = this.imageJobs.get(path);
    if (pending) return pending;
    const generation = this.generation,
      partition = this.partition;
    const job = (async () => {
      let blob = await this.dependencies.cache.image(partition, path);
      if (!blob) {
        if (generation !== this.generation || !this.online) return null;
        const response = await this.dependencies.fetcher(path, {
          credentials: 'include',
          signal: AbortSignal.any([this.controller.signal, AbortSignal.timeout(15_000)]),
          redirect: 'error',
        });
        if (generation !== this.generation) {
          await response.body?.cancel();
          return null;
        }
        if (response.status === 404 || response.status === 410) this.contentFailed(descriptor.host);
        if (
          !response.ok ||
          response.headers.get('content-type')?.split(';')[0] !== 'image/png' ||
          Number(response.headers.get('content-length') ?? 0) > 256 * 1024
        )
          return null;
        const reader = response.body?.getReader();
        if (!reader) return null;
        const chunks: Uint8Array<ArrayBuffer>[] = [];
        let size = 0;
        for (;;) {
          const part = await reader.read();
          if (part.done) break;
          size += part.value.length;
          if (size > 256 * 1024) {
            await reader.cancel();
            return null;
          }
          chunks.push(new Uint8Array(part.value));
        }
        blob = new Blob(chunks, { type: 'image/png' });
        if (generation !== this.generation) return null;
        void this.dependencies.cache.save(partition, 'image', path, blob);
      }
      if (generation !== this.generation) return null;
      const url = URL.createObjectURL(blob);
      this.images.set(path, url);
      return url;
    })()
      .catch(() => null)
      .finally(() => {
        if (generation === this.generation) this.imageJobs.delete(path);
      });
    this.imageJobs.set(path, job);
    return job;
  }
}
export const siteFavicons = new SiteFaviconStore();
